import React, { useState, useEffect, useCallback } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query'
import './theme/types' // Import theme type extensions
import { AuthProvider } from '@/contexts/AuthContext'
import { SearchProvider } from '@/contexts/SearchContext'
import { formatApiError } from '@/utils/format-api-error'
import { showNotification, onNotification } from '@/utils/notification'

import { ErrorBoundary, AuthGuard } from '@/components/common'

// Import pages
import Home from '@/pages/Home'
import Dashboard from '@/pages/Dashboard'
import Profile from '@/pages/Profile'
import Bookings from '@/pages/Bookings'
import MyBookings from '@/pages/MyBookings'
import MyTrips from '@/pages/MyTrips'
import BookingDetail from '@/pages/BookingDetail'
import Calendar from '@/pages/Calendar'
import Chat from '@/pages/Chat'
import Unauthorized from '@/pages/Unauthorized'

// Auth pages
import Login from '@/pages/auth/Login'
import Register from '@/pages/auth/Register'
import ForgotPassword from '@/pages/auth/ForgotPassword'
import ResetPassword from '@/pages/auth/ResetPassword'

// Check-in page (public)
import CheckIn from '@/pages/CheckIn'

// Listings pages
import ListingsIndex from '@/pages/listings/ListingsIndex'
import ListingDetail from '@/pages/listings/ListingDetail'
import NewListing from '@/pages/listings/NewListing'


// Expense Management
import ExpenseManagement from '@/pages/ExpenseManagement'

// Financial Dashboard
import FinancialDashboard from '@/pages/FinancialDashboard'

// Booking Platform Pages
import ListingsPage from '@/pages/ListingsPage'
import PropertyDetailPage from '@/pages/PropertyDetailPage'
import InquiriesPage from '@/pages/InquiriesPage'

// Employee Routes
import { EmployeeRoutes } from '@/components/Layout'

const theme = createTheme({
  zIndex: {
    appBar: 1400, // Ensure AppBar is always on top
    drawer: 1200,
    modal: 1300,
    snackbar: 1500,
    tooltip: 1600,
  },
  palette: {
    mode: 'light',
    primary: {
      main: '#f59842',
      light: '#f7b06e',
      dark: '#d4781e',
      // Add lighter variant for calendar
      lighter: '#fef3e8',
    },
    secondary: {
      main: '#6b7280',
      light: '#9ca3af',
      dark: '#374151',
    },
    info: {
      main: '#0288d1',
      light: '#03dac6',
      dark: '#01579b',
    },
    success: {
      main: '#2e7d32',
      light: '#4caf50',
      dark: '#1b5e20',
    },
    warning: {
      main: '#ed6c02',
      light: '#ff9800',
      dark: '#e65100',
    },
    error: {
      main: '#d32f2f',
      light: '#f44336',
      dark: '#c62828',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 500,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          borderRadius: 8,
          fontWeight: 500,
          transition: 'all 150ms ease',
        },
        containedPrimary: {
          backgroundColor: '#f59842',
          '&:hover': {
            backgroundColor: '#d4781e',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(245,152,66,0.4)',
          },
        },
        outlinedPrimary: {
          borderColor: '#f59842',
          color: '#f59842',
          '&:hover': {
            backgroundColor: 'rgba(245,152,66,0.08)',
            borderColor: '#d4781e',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          transition: 'all 150ms ease',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 8,
          transition: 'all 150ms ease',
          [theme.breakpoints.down('sm')]: {
            minHeight: 48,
          },
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            minHeight: 48,
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            minHeight: 48,
            display: 'flex',
            alignItems: 'center',
          },
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          boxShadow: 'none',
          color: 'inherit',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          transition: 'all 150ms ease',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 150ms ease',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          transition: 'all 150ms ease',
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
})

function App() {
  const [notification, setNotification] = useState<string | null>(null)

  const handleCloseNotification = useCallback(() => {
    setNotification(null)
  }, [])

  useEffect(() => {
    return onNotification((message) => {
      setNotification(message)
    })
  }, [])

  const [queryClient] = useState(() => new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        showNotification(formatApiError(error))
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        showNotification(formatApiError(error))
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000, // 10 minutes
        retry: (failureCount, error: unknown) => {
          const err = error as { status?: number }
          // Don't retry on 4xx errors (client errors)
          if (err?.status !== undefined && err.status >= 400 && err.status < 500) {
            return false
          }
          // Retry up to 3 times for other errors
          return failureCount < 3
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
      },
      mutations: {
        retry: (failureCount, error: unknown) => {
          const err = error as { status?: number }
          // Don't retry mutations on 4xx errors
          if (err?.status !== undefined && err.status >= 400 && err.status < 500) {
            return false
          }
          // Retry up to 2 times for other errors
          return failureCount < 2
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      },
    },
  }))

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <AuthProvider>
            <SearchProvider>
              <Routes>
              {/* Public routes */}
              <Route path="/" element={<Home />} />
              <Route path="/unauthorized" element={<Unauthorized />} />
              
              {/* Check-in form - public route */}
              <Route path="/check-in/:token" element={<CheckIn />} />
              
              {/* Public booking platform routes */}
              <Route path="/search" element={<ListingsPage />} />
              <Route path="/listing/:id" element={<PropertyDetailPage />} />
              
              {/* Auth routes - redirect to dashboard if already authenticated */}
              <Route 
                path="/auth/login" 
                element={
                  <AuthGuard requireAuth={false} redirectTo="/">
                    <Login />
                  </AuthGuard>
                } 
              />
              <Route 
                path="/auth/register" 
                element={
                  <AuthGuard requireAuth={false} redirectTo="/">
                    <Register />
                  </AuthGuard>
                } 
              />
              <Route 
                path="/auth/forgot-password" 
                element={
                  <AuthGuard requireAuth={false} redirectTo="/">
                    <ForgotPassword />
                  </AuthGuard>
                } 
              />
              <Route 
                path="/auth/reset-password" 
                element={
                  <AuthGuard requireAuth={false} redirectTo="/">
                    <ResetPassword />
                  </AuthGuard>
                } 
              />
              
              {/* Protected routes */}
              <Route 
                path="/dashboard" 
                element={
                  <AuthGuard>
                    <Dashboard />
                  </AuthGuard>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <AuthGuard>
                    <Profile />
                  </AuthGuard>
                } 
              />
              {/* Legacy bookings route - redirect based on user role */}
              <Route 
                path="/bookings" 
                element={
                  <AuthGuard>
                    <Bookings />
                  </AuthGuard>
                } 
              />
              
              {/* New role-specific booking routes */}
              <Route 
                path="/my-bookings" 
                element={
                  <AuthGuard requiresHost={true}>
                    <MyBookings />
                  </AuthGuard>
                } 
              />
              <Route 
                path="/my-trips" 
                element={
                  <AuthGuard>
                    <MyTrips />
                  </AuthGuard>
                } 
              />
              
              <Route 
                path="/bookings/:bookingId" 
                element={
                  <AuthGuard requiresHost={true}>
                    <BookingDetail />
                  </AuthGuard>
                } 
              />
              <Route 
                path="/calendar" 
                element={
                  <AuthGuard requiresHost={true}>
                    <Calendar />
                  </AuthGuard>
                } 
              />
              <Route 
                path="/chat" 
                element={
                  <AuthGuard>
                    <Chat />
                  </AuthGuard>
                } 
              />
              
              {/* Listings routes - require host privileges */}
              <Route 
                path="/listings" 
                element={
                  <AuthGuard requiresHost={true}>
                    <ListingsIndex />
                  </AuthGuard>
                } 
              />
              <Route 
                path="/listings/new" 
                element={
                  <AuthGuard requiresHost={true}>
                    <NewListing />
                  </AuthGuard>
                } 
              />
              <Route 
                path="/listings/:id" 
                element={
                  <AuthGuard requiresHost={true}>
                    <ListingDetail />
                  </AuthGuard>
                } 
              />

              
              {/* Expense Management - require host privileges */}
              <Route 
                path="/expenses" 
                element={
                  <AuthGuard requiresHost={true}>
                    <ExpenseManagement />
                  </AuthGuard>
                } 
              />
              
              {/* Financial Dashboard - require host privileges */}
              <Route 
                path="/financial" 
                element={
                  <AuthGuard requiresHost={true}>
                    <FinancialDashboard />
                  </AuthGuard>
                } 
              />
              
              {/* Booking Inquiries - require host privileges */}
              <Route 
                path="/inquiries" 
                element={
                  <AuthGuard requiresHost={true}>
                    <InquiriesPage />
                  </AuthGuard>
                } 
              />
              
              {/* Employee/Admin routes - require employees user group */}
              <Route path="/admin/*" element={<EmployeeRoutes />} />
              
              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </SearchProvider>
          </AuthProvider>
        </ErrorBoundary>
        <Snackbar
          open={notification !== null}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseNotification} severity="error" variant="filled" sx={{ width: '100%' }}>
            {notification}
          </Alert>
        </Snackbar>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export default App